/**
 * Las cuentas de banco: as many as the shop really has (the owner, 2026-10-10: «we need the ability
 * to setup multiple bank accounts as that is closest to reality»), a córdoba and a dollar account
 * in a Nicaraguan bank and a dollar account in one in the United States, say.
 *
 * Each is an account of its own on the books, so each is squared with its own statement: two
 * accounts kept as one «Bancos» can be squared with neither. Up to five in córdobas and five in
 * dollars, already in the chart (1150–1154, 1155–1159): the first of each is the «Bancos» and the
 * «Banco en dólares» every shop always had, so whatever was booked to them stays where it was and
 * every record from before reads as it did. The others are opened, and named, by the owner
 * (`cuentasBanco`).
 *
 * A dollar account keeps its dollars on the books as well as their worth in córdobas: every line of
 * the journal on one carries the dollars it moved (`JournalLine.dolares`), so what it holds in
 * dollars is a sum, like any balance, and its statement in dollars is squared against that.
 */

import { z } from 'zod';

export const CUENTAS_BANCO_CORDOBAS = ['BANCOS', 'BANCOS_2', 'BANCOS_3', 'BANCOS_4', 'BANCOS_5'] as const;
export const CUENTAS_BANCO_DOLARES = [
  'BANCO_DOLARES',
  'BANCO_DOLARES_2',
  'BANCO_DOLARES_3',
  'BANCO_DOLARES_4',
  'BANCO_DOLARES_5',
] as const;
/** In the chart's order: the córdoba accounts, then the dollar ones. */
export const CUENTAS_BANCO = [...CUENTAS_BANCO_CORDOBAS, ...CUENTAS_BANCO_DOLARES] as const;

export type CuentaBancoCordobas = (typeof CUENTAS_BANCO_CORDOBAS)[number];
export type CuentaBancoDolares = (typeof CUENTAS_BANCO_DOLARES)[number];
export type CuentaBanco = (typeof CUENTAS_BANCO)[number];

export const cuentaBancoSchema = z.enum(CUENTAS_BANCO);
export const cuentaBancoCordobasSchema = z.enum(CUENTAS_BANCO_CORDOBAS);
export const cuentaBancoDolaresSchema = z.enum(CUENTAS_BANCO_DOLARES);

/** The first of each currency: the shop always has them, opened or not. */
export const CUENTAS_DE_SIEMPRE: readonly CuentaBanco[] = ['BANCOS', 'BANCO_DOLARES'];

export function esCuentaEnDolares(cuenta: string): cuenta is CuentaBancoDolares {
  return (CUENTAS_BANCO_DOLARES as readonly string[]).includes(cuenta);
}

export function esCuentaDeBanco(cuenta: string): cuenta is CuentaBanco {
  return (CUENTAS_BANCO as readonly string[]).includes(cuenta);
}

export const monedaDeCuenta = (cuenta: CuentaBanco): 'NIO' | 'USD' => (esCuentaEnDolares(cuenta) ? 'USD' : 'NIO');

/** One the owner opened, or named (`cuentasBanco`). */
export const cuentaBancoConfigSchema = z.object({
  cuenta: cuentaBancoSchema,
  /** What the owner calls it: «BAC córdobas», «Banpro dólares», «Cuenta en EE. UU.». */
  nombre: z.string().trim().min(1).max(40),
  /** Closed: offered nowhere, and still on the books with what it holds and everything it moved. */
  activa: z.boolean().default(true),
});
export type CuentaBancoConfig = z.infer<typeof cuentaBancoConfigSchema>;

export const cuentasBancoSchema = z
  .array(cuentaBancoConfigSchema)
  .max(CUENTAS_BANCO.length)
  .refine((lista) => new Set(lista.map((c) => c.cuenta)).size === lista.length, {
    message: 'Una cuenta de banco aparece dos veces',
  });

/** A bank account of the shop's, as the screens offer it. */
export interface CuentaDeBanco {
  cuenta: CuentaBanco;
  moneda: 'NIO' | 'USD';
  /** The owner's name for it, or null: the first of each currency is «Banco» or «Banco en dólares» until named. */
  nombre: string | null;
  activa: boolean;
}

/**
 * The shop's bank accounts, in the chart's order: the first of each currency always, the others
 * once opened (`cuentasBanco`).
 */
export function cuentasDeBanco(lista: readonly CuentaBancoConfig[]): CuentaDeBanco[] {
  const porCuenta = new Map(lista.map((c) => [c.cuenta, c]));
  return CUENTAS_BANCO.flatMap((cuenta): CuentaDeBanco[] => {
    const puesta = porCuenta.get(cuenta);
    if (!puesta && !CUENTAS_DE_SIEMPRE.includes(cuenta)) return [];
    return [{ cuenta, moneda: monedaDeCuenta(cuenta), nombre: puesta?.nombre ?? null, activa: puesta?.activa ?? true }];
  });
}

/** Whether `cuenta` is one of the shop's, open or closed: one never opened takes nothing. */
export function cuentaAbierta(lista: readonly CuentaBancoConfig[], cuenta: CuentaBanco): boolean {
  return CUENTAS_DE_SIEMPRE.includes(cuenta) || lista.some((c) => c.cuenta === cuenta);
}

/** The next account of `moneda` to open, or null when all five are. */
export function siguienteCuenta(lista: readonly CuentaBancoConfig[], moneda: 'NIO' | 'USD'): CuentaBanco | null {
  const todas = moneda === 'USD' ? CUENTAS_BANCO_DOLARES : CUENTAS_BANCO_CORDOBAS;
  return todas.find((c) => !cuentaAbierta(lista, c)) ?? null;
}
